import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2, MapPin, Clock, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Mixed', 'Men', 'Women', 'Youth', 'Couples', 'Young Adults', 'Seniors'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminSmallGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    leader: '',
    day: 'Wednesday',
    time: '',
    location: '',
    category: 'Mixed',
    is_active: true,
    is_accepting_members: true,
    max_members: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/features/small-groups`);
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to load small groups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        max_members: formData.max_members ? parseInt(formData.max_members) : null
      };
      
      if (editingGroup) {
        await axios.put(`${API}/features/small-groups/${editingGroup.id}`, data, { withCredentials: true });
        toast.success('Group updated');
      } else {
        await axios.post(`${API}/features/small-groups`, data, { withCredentials: true });
        toast.success('Group created');
      }
      setShowDialog(false);
      resetForm();
      fetchGroups();
    } catch (error) {
      toast.error('Failed to save group');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this small group?')) return;
    try {
      await axios.delete(`${API}/features/small-groups/${id}`, { withCredentials: true });
      toast.success('Group deleted');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const openEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name || '',
      description: group.description || '',
      leader: group.leader || '',
      day: group.day || 'Wednesday',
      time: group.time || '',
      location: group.location || '',
      category: group.category || 'Mixed',
      is_active: group.is_active !== false,
      is_accepting_members: group.is_accepting_members !== false,
      max_members: group.max_members || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      leader: '',
      day: 'Wednesday',
      time: '',
      location: '',
      category: 'Mixed',
      is_active: true,
      is_accepting_members: true,
      max_members: ''
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Mixed': 'bg-gold/10 text-gold',
      'Men': 'bg-blue-100 text-blue-600',
      'Women': 'bg-pink-100 text-pink-600',
      'Youth': 'bg-purple/10 text-purple',
      'Couples': 'bg-burgundy/10 text-burgundy',
      'Young Adults': 'bg-orange-100 text-orange-600',
      'Seniors': 'bg-olive/10 text-olive'
    };
    return colors[category] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-small-groups-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Small Groups</h1>
          <p className="text-charcoal/60">Manage small group listings</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-group-btn"
        >
          <Plus size={18} className="mr-2" /> Add Group
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {groups.map((group) => (
          <motion.div
            key={group.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-serif text-lg text-charcoal">{group.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(group.category)}`}>
                    {group.category}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-sm text-charcoal/60">
                  <User size={14} /> Led by {group.leader}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(group)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(group.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 text-sm text-charcoal/60 mb-3">
              <span className="flex items-center gap-1">
                <Clock size={14} /> {group.day}s at {group.time}
              </span>
              <span className="flex items-center gap-1">
                <MapPin size={14} /> {group.location}
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              {!group.is_active && (
                <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600">Inactive</span>
              )}
              {group.is_accepting_members ? (
                <span className="text-xs px-2 py-1 rounded-full bg-olive/10 text-olive">Accepting Members</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Group Full</span>
              )}
              {group.max_members && (
                <span className="text-xs text-charcoal/50">
                  {group.current_members || 0}/{group.max_members} members
                </span>
              )}
            </div>
          </motion.div>
        ))}
        {groups.length === 0 && (
          <div className="col-span-2 text-center py-12 text-charcoal/50">
            <Users size={48} className="mx-auto mb-4 opacity-20" />
            <p>No small groups yet. Create your first group!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingGroup ? 'Edit Small Group' : 'Create Small Group'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Leader Name *</Label>
                <Input
                  value={formData.leader}
                  onChange={(e) => setFormData({...formData, leader: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Day *</Label>
                <Select value={formData.day} onValueChange={(v) => setFormData({...formData, day: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  placeholder="7:00 PM"
                  required
                />
              </div>
              <div>
                <Label>Max Members</Label>
                <Input
                  type="number"
                  value={formData.max_members}
                  onChange={(e) => setFormData({...formData, max_members: e.target.value})}
                  placeholder="Unlimited"
                />
              </div>
            </div>
            <div>
              <Label>Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Meeting location"
                required
              />
            </div>
            <div>
              <Label>Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Group description..."
                height="120px"
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_accepting"
                  checked={formData.is_accepting_members}
                  onChange={(e) => setFormData({...formData, is_accepting_members: e.target.checked})}
                />
                <Label htmlFor="is_accepting">Accepting New Members</Label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
